import { useMemo, useState } from "react";
import { Info, Search } from "lucide-react";
import DrawdownMeter from "../components/DrawdownMeter";
import Pagination from "../components/Pagination";
import StatCard, { StatGrid } from "../components/StatCard";
import Tag from "../components/Tag";
import TextLink from "../components/TextLink";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import { formatNumber, formatUsd } from "../format";
import useApiQuery from "../useApiQuery";
import styles from "./LeaderboardPage.module.css";

const PAGE_SIZE = 10;

// Widths include the cell gutters and add up to the table's min-width
const COLUMNS = [
  { label: "Rank", width: 88 },
  { label: "Name", width: 180 },
  { label: "PRP profit", width: 160, hint: "Profit on the trader's current account, in PRP" },
  { label: "Status", width: 140 },
  { label: "USDC paid", width: 140 },
  { label: "Daily DD", width: 200 },
  { label: "Max DD", width: 200 },
];

const MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };

// A trader who has used three quarters of either drawdown limit is flagged before they breach
const CRITICAL_RATIO = 0.75;

const STATUS = {
  active: { label: "Active", tone: "success" },
  critical: { label: "Critical", tone: "warning" },
  breached: { label: "Breached", tone: "danger" },
};

function traderStatus(trader, rules) {
  if (trader.status === "breached") {
    return "breached";
  }
  const nearDaily = rules?.dailyLossPct && trader.daily / rules.dailyLossPct >= CRITICAL_RATIO;
  const nearMax = rules?.maxDrawdownPct && trader.max / rules.maxDrawdownPct >= CRITICAL_RATIO;
  return nearDaily || nearMax ? "critical" : "active";
}

const formatPrp = (value) => (value === null || value === undefined ? "—" : `${formatNumber(value)} PRP`);

export default function LeaderboardPage() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  // The board is cached server-side for 5 minutes, so it's fetched whole and searched here
  const { data, status, reload } = useApiQuery("/api/leaderboard");

  const traders = useMemo(() => data?.traders ?? [], [data]);
  const rules = data?.rules;
  const totalProfit = useMemo(() => traders.reduce((sum, trader) => sum + (Number(trader.prop) || 0), 0), [traders]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return normalized ? traders.filter((trader) => trader.name.toLowerCase().includes(normalized)) : traders;
  }, [traders, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const stats = [
    { label: "Pool balance", value: formatUsd(data?.overview?.poolBalance), tone: "brand" },
    { label: "Funded traders", value: formatNumber(data?.overview?.qualifiedTraders) },
    { label: "Total profit in $PROP", value: data ? formatNumber(totalProfit) : "—", tone: "brand", note: "Across all funded accounts" },
    { label: "Total USDC payouts", value: formatUsd(data?.overview?.totalUsdcPaid) },
  ];

  return (
    <section className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.title}>Leaderboard</h1>
        <div className={`${styles.status} d-flex align-items-center`}>
          <Tag tone="brand" dot>
            Live
          </Tag>
          <p className={styles.meta}>Updates every 5 minutes</p>
        </div>
      </header>

      <div className={styles.stats}>
        <StatGrid>
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </StatGrid>
      </div>

      <section>
        <div className={`${controlStyles.toolbar} ${styles.toolbar} d-flex align-items-center justify-content-between`}>
          <h2 className={styles.sectionTitle}>Traders</h2>
          <label className={`${controlStyles.search} d-flex align-items-center`}>
            <Search size={20} strokeWidth={1.75} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search traders"
              aria-label="Search traders"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>

        <div className={tableStyles.wrap}>
          <table className={`${tableStyles.table} ${tableStyles.fixed} ${styles.table}`}>
            <colgroup>
              {COLUMNS.map((column) => (
                <col key={column.label} style={{ width: column.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column.label} scope="col">
                    {column.hint ? (
                      <span className="d-inline-flex align-items-center gap-1" title={column.hint}>
                        {column.label}
                        <Info size={12} strokeWidth={2} aria-label={column.hint} />
                      </span>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((trader) => {
                const traderState = STATUS[traderStatus(trader, rules)];

                return (
                  <tr key={trader.rank}>
                    <td className={styles.rank}>
                      {MEDALS[trader.rank] ? `${MEDALS[trader.rank]} ` : ""}
                      {trader.rank}
                    </td>
                    <td className={styles.name}>{trader.name}</td>
                    <td className={trader.prop >= 0 ? styles.profit : styles.loss}>{formatPrp(trader.prop)}</td>
                    <td>
                      <Tag tone={traderState.tone}>{traderState.label}</Tag>
                    </td>
                    <td className={styles.paid}>{formatUsd(trader.usdcPaid)}</td>
                    <td>
                      <DrawdownMeter value={trader.daily} limit={rules?.dailyLossPct} label="Daily drawdown" />
                    </td>
                    <td>
                      <DrawdownMeter value={trader.max} limit={rules?.maxDrawdownPct} label="Max drawdown" />
                    </td>
                  </tr>
                );
              })}
              {status === "loading" && !data ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    Loading the leaderboard…
                  </td>
                </tr>
              ) : status === "error" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    We couldn't load the leaderboard.{" "}
                    <TextLink arrow={false} onClick={reload}>
                      Try again
                    </TextLink>
                  </td>
                </tr>
              ) : data && visible.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    {query ? "No traders match this search." : "No traders on the board yet."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
      </section>
    </section>
  );
}
